import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Building, 
  Phone, 
  Mail, 
  X, 
  Check, 
  RefreshCw, 
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Download,
  Eye,
  Truck,
  CreditCard,
  Banknote,
  Receipt,
  Package,
  Users
} from "lucide-react";
import axios from "axios";

const Suppliers = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [detailSupplier, setDetailSupplier] = useState(null);
  
  // Multi-select
  const [selectedSuppliers, setSelectedSuppliers] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    org_number: "",
    city: "",
    email: ""
  });
  
  const [formData, setFormData] = useState({
    // Basic info
    supplier_number: "",
    name: "",
    org_number: "",
    vat_number: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "Sverige",
    contact_person: "",
    
    // Payment info
    payment_terms: 30,
    bank_account: "",
    bank_name: "",
    iban: "",
    bic_swift: "",
    currency: "SEK",
    
    // Accounting
    default_expense_account: "4000",
    
    // Category & Notes
    category: "goods",
    notes: "",
  });
  
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/suppliers/stats/overview`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      // Fallback to regular endpoint
      try {
        const response = await axios.get(`${API}/suppliers`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setSuppliers(response.data.map(s => ({
          ...s,
          total_purchases: 0,
          invoice_count: 0,
          outstanding_amount: 0,
          supplier_status: "unknown"
        })));
      } catch (e) {
        toast.error(t('suppliers.fetchError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!formData.org_number || formData.org_number.length < 10) {
      toast.error(t('suppliers.invalidOrgNumber'));
      return;
    }
    
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers/lookup/${formData.org_number}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          name: response.data.name || prev.name,
          address: response.data.address || prev.address,
          postal_code: response.data.postal_code || prev.postal_code,
          city: response.data.city || prev.city,
          vat_number: response.data.vat_number || prev.vat_number,
        }));
        toast.success(t('suppliers.infoFetched'));
      }
    } catch (error) {
      toast.error(t('suppliers.lookupFailed'));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('suppliers.updated'));
      } else {
        await axios.post(`${API}/suppliers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('suppliers.created'));
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('suppliers.saveFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('suppliers.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('suppliers.deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('suppliers.deleteFailed'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(t('suppliers.confirmBulkDelete', { count: selectedSuppliers.size }))) return;
    
    try {
      await Promise.all(
        Array.from(selectedSuppliers).map(id =>
          axios.delete(`${API}/suppliers/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(t('suppliers.bulkDeleted', { count: selectedSuppliers.size }));
      setSelectedSuppliers(new Set());
      fetchData();
    } catch (error) {
      toast.error(t('suppliers.bulkDeleteFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_number: "",
      name: "",
      org_number: "",
      vat_number: "",
      email: "",
      phone: "",
      address: "",
      postal_code: "",
      city: "",
      country: "Sverige",
      contact_person: "",
      payment_terms: 30,
      bank_account: "",
      bank_name: "",
      iban: "",
      bic_swift: "",
      currency: "SEK",
      default_expense_account: "4000",
      category: "goods",
      notes: "",
    });
    setEditingSupplier(null);
    setActiveTab("basic");
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_number: supplier.supplier_number || "",
      name: supplier.name || "",
      org_number: supplier.org_number || "",
      vat_number: supplier.vat_number || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      postal_code: supplier.postal_code || "",
      city: supplier.city || "",
      country: supplier.country || "Sverige",
      contact_person: supplier.contact_person || "",
      payment_terms: supplier.payment_terms || 30,
      bank_account: supplier.bank_account || "",
      bank_name: supplier.bank_name || "",
      iban: supplier.iban || "",
      bic_swift: supplier.bic_swift || "",
      currency: supplier.currency || "SEK",
      default_expense_account: supplier.default_expense_account || "4000",
      category: supplier.category || "goods",
      notes: supplier.notes || "",
    });
    setDialogOpen(true);
  };

  const handleNewSupplier = async () => {
    resetForm();
    // Get next supplier number
    try {
      const response = await axios.get(`${API}/suppliers/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, supplier_number: response.data.next_number }));
    } catch (e) {
      // Ignore - number will be auto-generated
    }
    setDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1" /> 
      : <ArrowDown size={14} className="ml-1" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedSuppliers.size === filteredAndSortedSuppliers.length) {
      setSelectedSuppliers(new Set());
    } else {
      setSelectedSuppliers(new Set(filteredAndSortedSuppliers.map(s => s.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedSuppliers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedSuppliers(newSelected);
  };

  // Filter and sort
  const filteredAndSortedSuppliers = useMemo(() => {
    let result = [...suppliers];
    
    // Global search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s => 
        s.name?.toLowerCase().includes(term) ||
        s.org_number?.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term) ||
        s.city?.toLowerCase().includes(term) ||
        s.supplier_number?.toLowerCase().includes(term)
      );
    }
    
    // Column filters
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value) {
        result = result.filter(s => 
          s[key]?.toLowerCase().includes(value.toLowerCase())
        );
      }
    });
    
    // Status filter
    if (filterStatus !== "all") {
      result = result.filter(s => s.supplier_status === filterStatus);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      if (typeof aVal === "string") aVal = aVal?.toLowerCase() || "";
      if (typeof bVal === "string") bVal = bVal?.toLowerCase() || "";
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [suppliers, searchTerm, columnFilters, filterStatus, sortColumn, sortDirection]);

  // Stats
  const stats = useMemo(() => {
    const total = suppliers.length;
    const totalPurchases = suppliers.reduce((sum, s) => sum + (s.total_purchases || 0), 0);
    const outstanding = suppliers.reduce((sum, s) => sum + (s.outstanding_amount || 0), 0);
    const activeCount = suppliers.filter(s => s.invoice_count > 0).length;
    
    return { total, totalPurchases, outstanding, activeCount };
  }, [suppliers]);

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-green-100 text-green-700">{t('suppliers.status.active')}</Badge>,
      new: <Badge className="bg-blue-100 text-blue-700">{t('suppliers.status.new')}</Badge>,
      has_outstanding: <Badge className="bg-amber-100 text-amber-700">{t('suppliers.status.hasOutstanding')}</Badge>,
      unknown: <Badge variant="outline">{t('suppliers.status.unknown')}</Badge>
    };
    return badges[status] || badges.unknown;
  };

  const getCategoryBadge = (category) => {
    const badges = {
      goods: <Badge variant="outline" className="text-blue-600">{t('suppliers.category.goods')}</Badge>,
      services: <Badge variant="outline" className="text-purple-600">{t('suppliers.category.services')}</Badge>,
      both: <Badge variant="outline" className="text-indigo-600">{t('suppliers.category.both')}</Badge>
    };
    return badges[category] || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="suppliers">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('suppliers.title')}</h1>
          <p className="text-slate-500 mt-1">{suppliers.length} {t('suppliers.registered')}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => fetchData()} data-testid="refresh-btn">
            <RefreshCw size={16} className="mr-2" />
            {t('common.refresh')}
          </Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={handleNewSupplier}
            data-testid="new-supplier-btn"
          >
            <Plus size={16} className="mr-2" />
            {t('suppliers.newSupplier')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('suppliers.stats.total')}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Users className="text-indigo-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('suppliers.stats.totalPurchases')}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalPurchases)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('suppliers.stats.outstanding')}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.outstanding)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Receipt className="text-amber-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('suppliers.stats.active')}</p>
                <p className="text-2xl font-bold">{stats.activeCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="text-blue-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      {selectedSuppliers.size > 0 && (
        <Card className="card-base border-indigo-200 bg-indigo-50">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-indigo-700">
                {selectedSuppliers.size} {t('common.selected')}
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setSelectedSuppliers(new Set())}
                >
                  <X size={14} className="mr-1" />
                  {t('common.clearSelection')}
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={handleBulkDelete}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 size={14} className="mr-1" />
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters & Search */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder={t('suppliers.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-suppliers"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <Filter size={14} className="mr-2" />
                <SelectValue placeholder={t('suppliers.filterByStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="active">{t('suppliers.status.active')}</SelectItem>
                <SelectItem value="new">{t('suppliers.status.new')}</SelectItem>
                <SelectItem value="has_outstanding">{t('suppliers.status.hasOutstanding')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Suppliers Table */}
      <Card className="card-base overflow-hidden" data-testid="suppliers-table">
        <CardContent className="p-0">
          {filteredAndSortedSuppliers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedSuppliers.size === filteredAndSortedSuppliers.length && filteredAndSortedSuppliers.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button 
                        onClick={() => handleSort("supplier_number")} 
                        className="flex items-center hover:text-slate-700"
                      >
                        {t('suppliers.supplierNumber')}
                        <SortIcon column="supplier_number" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button 
                        onClick={() => handleSort("name")} 
                        className="flex items-center hover:text-slate-700"
                      >
                        {t('suppliers.name')}
                        <SortIcon column="name" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      <button 
                        onClick={() => handleSort("city")} 
                        className="flex items-center hover:text-slate-700"
                      >
                        {t('common.city')}
                        <SortIcon column="city" />
                      </button>
                    </th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">
                      {t('suppliers.category.label')}
                    </th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-3">
                      <button 
                        onClick={() => handleSort("total_purchases")} 
                        className="flex items-center justify-end hover:text-slate-700 ml-auto"
                      >
                        {t('suppliers.totalPurchases')}
                        <SortIcon column="total_purchases" />
                      </button>
                    </th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase p-3">
                      {t('suppliers.outstanding')}
                    </th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase p-3">
                      {t('common.status')}
                    </th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedSuppliers.map((supplier) => (
                    <tr 
                      key={supplier.id} 
                      className="border-b hover:bg-slate-50 transition-colors"
                      data-testid={`supplier-row-${supplier.id}`}
                    >
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedSuppliers.has(supplier.id)}
                          onCheckedChange={() => toggleSelect(supplier.id)}
                        />
                      </td>
                      <td className="p-3 font-mono text-sm text-slate-600">
                        {supplier.supplier_number || "-"}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-medium text-sm">
                            {supplier.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{supplier.name}</p>
                            {supplier.org_number && (
                              <p className="text-xs text-slate-500 font-mono">{supplier.org_number}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-slate-600">{supplier.city || "-"}</td>
                      <td className="p-3">{getCategoryBadge(supplier.category)}</td>
                      <td className="p-3 text-right font-mono">
                        {formatCurrency(supplier.total_purchases)}
                      </td>
                      <td className="p-3 text-right font-mono">
                        {supplier.outstanding_amount > 0 ? (
                          <span className="text-amber-600">{formatCurrency(supplier.outstanding_amount)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {getStatusBadge(supplier.supplier_status)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setDetailSupplier(supplier)}
                            data-testid={`view-supplier-${supplier.id}`}
                          >
                            <Eye size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(supplier)}
                            data-testid={`edit-supplier-${supplier.id}`}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(supplier.id)}
                            data-testid={`delete-supplier-${supplier.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Truck className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500">{t('suppliers.noSuppliers')}</p>
              <Button 
                className="mt-4 bg-indigo-600 hover:bg-indigo-700" 
                onClick={handleNewSupplier}
              >
                <Plus size={16} className="mr-2" />
                {t('suppliers.addFirst')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={20} />
              {editingSupplier ? t('suppliers.editSupplier') : t('suppliers.newSupplier')}
            </DialogTitle>
            <DialogDescription>
              {editingSupplier ? t('suppliers.editDescription') : t('suppliers.createDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="basic">{t('suppliers.tabs.basic')}</TabsTrigger>
              <TabsTrigger value="payment">{t('suppliers.tabs.payment')}</TabsTrigger>
              <TabsTrigger value="other">{t('suppliers.tabs.other')}</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit}>
              <TabsContent value="basic" className="space-y-4">
                {/* Org number with lookup */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('suppliers.orgNumber')}</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.org_number}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, org_number: val });
                          if (val.replace(/\D/g, "").length === 10) {
                            handleLookup();
                          }
                        }}
                        placeholder="556123-4567"
                        data-testid="org-number-input"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={handleLookup}
                        disabled={lookupLoading}
                      >
                        {lookupLoading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">{t('suppliers.orgNumberHelp')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('suppliers.supplierNumber')}</Label>
                    <Input
                      value={formData.supplier_number}
                      onChange={(e) => setFormData({ ...formData, supplier_number: e.target.value })}
                      placeholder="L1"
                      data-testid="supplier-number-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('suppliers.name')} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('suppliers.namePlaceholder')}
                    required
                    data-testid="supplier-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.email')}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="leverantor@foretag.se"
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('common.phone')}</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="08-123 456 78"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('common.address')}</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Leverantörsgatan 1"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.postalCode')}</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="111 22"
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('common.city')}</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Stockholm"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('common.country')}</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="Sverige"
                      data-testid="country-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('suppliers.contactPerson')}</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    placeholder="Anna Andersson"
                    data-testid="contact-person-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="payment" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('suppliers.paymentTerms')}</Label>
                    <Select 
                      value={String(formData.payment_terms)} 
                      onValueChange={(v) => setFormData({ ...formData, payment_terms: parseInt(v) })}
                    >
                      <SelectTrigger data-testid="payment-terms-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 {t('common.days')}</SelectItem>
                        <SelectItem value="15">15 {t('common.days')}</SelectItem>
                        <SelectItem value="20">20 {t('common.days')}</SelectItem>
                        <SelectItem value="30">30 {t('common.days')}</SelectItem>
                        <SelectItem value="45">45 {t('common.days')}</SelectItem>
                        <SelectItem value="60">60 {t('common.days')}</SelectItem>
                        <SelectItem value="90">90 {t('common.days')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('suppliers.currency')}</Label>
                    <Select 
                      value={formData.currency} 
                      onValueChange={(v) => setFormData({ ...formData, currency: v })}
                    >
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SEK">SEK</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="NOK">NOK</SelectItem>
                        <SelectItem value="DKK">DKK</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('suppliers.bankAccount')}</Label>
                  <Input
                    value={formData.bank_account}
                    onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                    placeholder="1234-5678"
                    data-testid="bank-account-input"
                  />
                  <p className="text-xs text-slate-500">{t('suppliers.bankAccountHelp')}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t('suppliers.bankName')}</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder="Swedbank, SEB, Nordea..."
                    data-testid="bank-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IBAN</Label>
                    <Input
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      placeholder="SE00 0000 0000 0000 0000 0000"
                      data-testid="iban-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>BIC/SWIFT</Label>
                    <Input
                      value={formData.bic_swift}
                      onChange={(e) => setFormData({ ...formData, bic_swift: e.target.value })}
                      placeholder="SWEDSESS"
                      data-testid="bic-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('suppliers.vatNumber')}</Label>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    placeholder="SE556123456701"
                    data-testid="vat-number-input"
                  />
                </div>
              </TabsContent>

              <TabsContent value="other" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('suppliers.category.label')}</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="goods">{t('suppliers.category.goods')}</SelectItem>
                        <SelectItem value="services">{t('suppliers.category.services')}</SelectItem>
                        <SelectItem value="both">{t('suppliers.category.both')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('suppliers.defaultExpenseAccount')}</Label>
                    <Input
                      value={formData.default_expense_account}
                      onChange={(e) => setFormData({ ...formData, default_expense_account: e.target.value })}
                      placeholder="4000"
                      data-testid="expense-account-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('suppliers.notes')}</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('suppliers.notesPlaceholder')}
                    rows={4}
                    data-testid="notes-input"
                  />
                </div>
              </TabsContent>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-supplier-btn">
                  {editingSupplier ? t('common.save') : t('suppliers.createSupplier')}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailSupplier} onOpenChange={(open) => !open && setDetailSupplier(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold">
                {detailSupplier?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              {detailSupplier?.name}
            </DialogTitle>
          </DialogHeader>
          
          {detailSupplier && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">{t('suppliers.totalPurchases')}</p>
                  <p className="text-xl font-bold">{formatCurrency(detailSupplier.total_purchases)}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">{t('suppliers.invoiceCount')}</p>
                  <p className="text-xl font-bold">{detailSupplier.invoice_count || 0}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">{t('suppliers.outstanding')}</p>
                  <p className="text-xl font-bold text-amber-600">
                    {formatCurrency(detailSupplier.outstanding_amount)}
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">{t('suppliers.contactInfo')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {detailSupplier.org_number && (
                    <div className="flex items-center gap-2">
                      <Building size={16} className="text-slate-400" />
                      <span className="font-mono">{detailSupplier.org_number}</span>
                    </div>
                  )}
                  {detailSupplier.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-slate-400" />
                      <a href={`mailto:${detailSupplier.email}`} className="text-indigo-600 hover:underline">
                        {detailSupplier.email}
                      </a>
                    </div>
                  )}
                  {detailSupplier.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-slate-400" />
                      <span>{detailSupplier.phone}</span>
                    </div>
                  )}
                  {detailSupplier.city && (
                    <div className="flex items-center gap-2">
                      <Truck size={16} className="text-slate-400" />
                      <span>{detailSupplier.address}, {detailSupplier.postal_code} {detailSupplier.city}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">{t('suppliers.paymentInfo')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-slate-400" />
                    <span>{detailSupplier.payment_terms} {t('common.days')}</span>
                  </div>
                  {detailSupplier.bank_account && (
                    <div className="flex items-center gap-2">
                      <Banknote size={16} className="text-slate-400" />
                      <span className="font-mono">{detailSupplier.bank_account}</span>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailSupplier(null)}>
                  {t('common.close')}
                </Button>
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => {
                    handleEdit(detailSupplier);
                    setDetailSupplier(null);
                  }}
                >
                  <Edit size={14} className="mr-2" />
                  {t('common.edit')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
